/**
 * The words of the agent card's «MCP calls» panel (`GET /api/v1/mcp-calls`, the worker's
 * lib/mcp-call-log.ts). 🔴 A row's agent is the one bound to the token's subject when the call
 * was logged; a call made before the binding, or by an unbound client, is not this agent's here.
 * 400, 429 and 503 are not only the MCP route's own refusals: v1 answers them too (a wrong query,
 * its own stats limit, X's credentials refused), so no word here says that v1 was not asked.
 */
import type { McpCallRow } from "./api.ts";

/** The most rows the panel asks for. */
export const MCP_CALLS_SHOWN = 50;

export const MCP_CALLS_TITLE = "MCP calls";

export const MCP_CALLS_ABOUT =
	"The MCP tool calls of the service client bound to this agent when each call was made, newest first: the tool, the status it was answered, how long it took in this console. A call with an unknown tool or a wrong argument is logged too, under the name it came with. Rows are kept 30 days.";

export const MCP_STATUS_HELP =
	"The status is v1's answer — 403 when the kit refuses a paused or retired agent — or this console's own before v1 is asked: 400 an unknown tool or a wrong argument, 429 over the rate limit, 503 the rate limit unavailable.";

export const MCP_CALLS_NONE = "No MCP call is logged for this agent.";

export const MCP_CALLS_UNAVAILABLE = "The MCP call log is not available:";

export const MCP_CALLS_NOT_READ = "The MCP calls could not be read.";

/** The instant as UTC, to the second — the log's own clock, not the viewer's zone. */
export function callTime(at: string): string {
	const t = Date.parse(at);
	if (Number.isNaN(t)) return at;
	return `${new Date(t).toISOString().slice(0, 19).replace("T", " ")} UTC`;
}

/** The status with what it is: answered (below 400) or an error. */
export function callStatus(row: Pick<McpCallRow, "status">): string {
	return row.status < 400 ? `${row.status} answered` : `${row.status} error`;
}

/**
 * How many rows are shown, and that older ones are not when the panel's limit is reached. The
 * card says it beside rows only: with none, the table's own empty message speaks.
 */
export function callsShownLine(n: number): string {
	if (n >= MCP_CALLS_SHOWN) return `The latest ${MCP_CALLS_SHOWN} calls; older ones are not shown.`;
	return n === 1 ? "1 call." : `${n} calls.`;
}
